import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { GraduationCap, Phone, MessageCircle, Search, Upload, MapPin, Users, Download } from "lucide-react";

const POSTER_URL = "/loan-mela-poster.jpg";

const STATUSES = ["Pending", "Invited", "Interested", "Not Interested"];
const STATUS_STYLE = {
  Pending: "text-slate-500 border-slate-200 bg-slate-50",
  Invited: "text-blue-700 border-blue-200 bg-blue-50",
  Interested: "text-emerald-700 border-emerald-200 bg-emerald-50",
  "Not Interested": "text-rose-700 border-rose-200 bg-rose-50",
};

const digits = (m) => String(m || "").replace(/\D/g, "");
const telLink = (m) => `tel:+91${digits(m).slice(-10)}`;
const waLink = (m, msg) => {
  const d = digits(m);
  const num = d.length === 10 ? "91" + d : d;
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
};

export default function SchoolDrive() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [schools, setSchools] = useState([]);
  const [publicToken, setPublicToken] = useState("");
  const [template, setTemplate] = useState("");
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [rmFilter, setRmFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const regLink = `${window.location.origin}/apply/${publicToken}`;
  const inviteMsg = (template || "").replace("{reg_link}", regLink);

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== "all") params.status = statusFilter;
    if (isAdmin && rmFilter !== "all") params.rm = rmFilter;
    api.get("/schools", { params }).then((r) => {
      setSchools(r.data.schools || []);
      setPublicToken(r.data.public_token || "");
      setTemplate(r.data.invite_template || "");
    }).finally(() => setLoading(false));
    if (isAdmin) api.get("/schools/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, statusFilter, rmFilter]);

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/schools/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.schools} schools, ${data.contacts} contacts across ${Object.keys(data.per_rm).length} RMs`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const setStatus = async (sid, cid, status) => {
    try {
      const { data } = await api.patch(`/schools/${sid}/contacts/${cid}`, { status });
      setSchools((prev) => prev.map((s) => (s.id === sid ? data : s)));
      if (status === "Invited" || status === "Interested") toast.success("Captured into Mela invited data ✓");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [bulk, setBulk] = useState(null); // { school, queue:[contacts], idx }
  const openBulk = (s) => {
    const pending = s.contacts.filter((c) => (c.status || "Pending") === "Pending");
    if (!pending.length) return;
    setBulk({ school: s, queue: pending, idx: 0 });
  };
  const bulkSendCurrent = async () => {
    const c = bulk.queue[bulk.idx];
    window.open(waLink(c.mobile, inviteMsg), "_blank"); // open first (keeps user-gesture, avoids popup block)
    try {
      const { data } = await api.patch(`/schools/${bulk.school.id}/contacts/${c.id}`, { status: "Invited" });
      setSchools((prev) => prev.map((s) => (s.id === bulk.school.id ? data : s)));
    } catch (e) { /* non-blocking */ }
    setBulk((b) => ({ ...b, idx: b.idx + 1 }));
  };
  const bulkSkip = () => setBulk((b) => ({ ...b, idx: b.idx + 1 }));

  return (
    <div data-testid="school-drive-page">
      <PageHeader
        title="School Drive — Sonarpur"
        subtitle={isAdmin ? "Import, distribute and track the school outreach campaign." : "Call & invite your assigned schools. Both teachers per school."}
        actions={isAdmin && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={doImport} data-testid="school-import-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={importing} className="bg-[#0A192F] hover:bg-[#0F4C81]" data-testid="school-import-btn">
              <Upload size={16} className="mr-2" />{importing ? "Importing…" : "Upload School Data"}
            </Button>
          </>
        )}
      />

      <Card className="p-4 mb-5 flex items-center gap-4" data-testid="poster-card">
        <img src={POSTER_URL} alt="Loan Mela 2026 Poster" className="w-16 h-24 object-cover rounded-lg border border-slate-200 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-800">Loan Mela 2026 — Event Poster</div>
          <div className="text-xs text-slate-500 mt-0.5">Download this poster, then attach it in the WhatsApp chat after it opens (WhatsApp links can't auto-attach images).</div>
        </div>
        <a href={POSTER_URL} download="DIGI-LOANS-Loan-Mela-2026.jpg" target="_blank" rel="noreferrer" data-testid="poster-download-btn"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-[#0A192F] text-white hover:bg-[#0F4C81] shrink-0">
          <Download size={15} /> Download Poster
        </a>
      </Card>

      {isAdmin && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <StatCard label="Schools" value={stats.total_schools} />
          <StatCard label="Contacts" value={stats.total_contacts} />
          <StatCard label="Invited" value={stats.status_counts?.Invited || 0} accent="text-blue-600" />
          <StatCard label="Interested" value={stats.status_counts?.Interested || 0} accent="text-emerald-600" />
        </div>
      )}

      {isAdmin && stats?.per_rm?.length > 0 && (
        <Card className="p-4 mb-5" data-testid="school-distribution">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Campaign progress by RM Group</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            {stats.per_rm.map((r) => {
              const pct = r.contacts ? Math.round((r.worked / r.contacts) * 100) : 0;
              return (
                <div key={r.rm_username} className="rounded-lg border border-slate-200 px-3 py-2.5" data-testid={`rm-progress-${r.rm_username}`}>
                  <div className="text-sm font-semibold text-slate-800 truncate">{r.rm_name}</div>
                  <div className="font-mono text-lg text-[#0F4C81]">{r.schools} <span className="text-xs text-slate-400 font-sans">schools</span></div>
                  <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1.5 mb-1">
                    <span>{r.worked}/{r.contacts} called</span><span className="font-mono font-semibold text-slate-600">{pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1.5">{r.invited} invited · {r.interested} interested</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search school name…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="school-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="school-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {isAdmin && stats?.per_rm && (
          <Select value={rmFilter} onValueChange={setRmFilter}>
            <SelectTrigger className="w-44" data-testid="school-rm-filter"><SelectValue placeholder="RM" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All RMs</SelectItem>
              {stats.per_rm.map((r) => <SelectItem key={r.rm_username} value={r.rm_username}>{r.rm_name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400 animate-pulse">Loading…</div>
      ) : schools.length === 0 ? (
        <Card className="p-12 text-center text-slate-400" data-testid="school-empty">
          <GraduationCap size={40} className="mx-auto mb-3 opacity-40" />
          {isAdmin ? "No school data yet. Click \"Upload School Data\" to import the Sonarpur Excel file." : "No schools assigned to you yet."}
        </Card>
      ) : (
        <>
          <div className="text-sm text-slate-500 mb-3">{schools.length} schools{schools.length > 150 ? " — showing first 150, refine with search" : ""}</div>
          <div className="space-y-4">
            {schools.slice(0, 150).map((s) => (
              <Card key={s.id} className="overflow-hidden" data-testid={`school-card-${s.id}`}>
                <div className="px-5 py-3 bg-slate-50 border-b flex items-center gap-2">
                  <GraduationCap size={16} className="text-[#0F4C81]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{s.sl_no}. {s.school_name}</div>
                    <div className="text-xs text-slate-400 flex items-center gap-1.5"><MapPin size={11} />{s.ward || s.circle} · {s.category}</div>
                  </div>
                  {(() => {
                    const pending = s.contacts.filter((c) => (c.status || "Pending") === "Pending").length;
                    return pending > 0 ? (
                      <Button size="sm" variant="outline" className="h-8 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => openBulk(s)} data-testid={`bulk-wa-${s.id}`}>
                        <MessageCircle size={13} className="mr-1" /> WhatsApp all pending ({pending})
                      </Button>
                    ) : null;
                  })()}
                  <span className="text-xs text-slate-400 flex items-center gap-1"><Users size={12} />{s.contacts.length}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {s.contacts.map((c) => (
                    <div key={c.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`contact-${c.id}`}>
                      <div className="flex-1 min-w-[160px]">
                        <div className="font-medium text-slate-800">{c.name}</div>
                        <div className="text-xs text-slate-400">{c.designation}</div>
                      </div>
                      <a href={telLink(c.mobile)} className="inline-flex items-center gap-1.5 text-sm font-mono text-[#0F4C81] hover:underline" data-testid={`call-${c.id}`}>
                        <Phone size={14} /> {c.mobile}
                      </a>
                      <a href={waLink(c.mobile, inviteMsg)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`wa-${c.id}`}>
                        <MessageCircle size={14} /> WhatsApp
                      </a>
                      <Select value={c.status || "Pending"} onValueChange={(v) => setStatus(s.id, c.id, v)}>
                        <SelectTrigger className={`w-40 h-9 text-xs border ${STATUS_STYLE[c.status || "Pending"]}`} data-testid={`status-${c.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!bulk} onOpenChange={(o) => !o && setBulk(null)}>
        <DialogContent data-testid="bulk-wa-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageCircle size={18} className="text-emerald-600" />Bulk WhatsApp</DialogTitle>
            <DialogDescription>{bulk?.school.school_name} — invite each pending teacher back-to-back. Each tap opens WhatsApp and marks them Invited.</DialogDescription>
          </DialogHeader>
          {bulk && (
            <div className="pt-1">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span>Progress</span><span className="font-mono" data-testid="bulk-wa-progress">{bulk.idx} / {bulk.queue.length} sent</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-4">
                <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${Math.round((bulk.idx / bulk.queue.length) * 100)}%` }} />
              </div>
              {bulk.idx < bulk.queue.length ? (
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="text-xs text-slate-400 mb-1">Next teacher ({bulk.idx + 1} of {bulk.queue.length})</div>
                  <div className="font-semibold text-slate-900">{bulk.queue[bulk.idx].name}</div>
                  <div className="text-xs text-slate-500">{bulk.queue[bulk.idx].designation} · {bulk.queue[bulk.idx].mobile}</div>
                  <div className="flex gap-2 mt-4">
                    <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={bulkSendCurrent} data-testid="bulk-wa-send">
                      <MessageCircle size={15} className="mr-1.5" /> Open WhatsApp &amp; Next
                    </Button>
                    <Button variant="outline" onClick={bulkSkip} data-testid="bulk-wa-skip">Skip</Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-emerald-600 font-semibold" data-testid="bulk-wa-done">All pending teachers messaged ✓</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ label, value, accent }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
    <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`font-mono font-bold text-xl mt-0.5 ${accent || "text-slate-900"}`}>{value}</div>
  </div>
);
